import React, { useEffect } from "react";
import {
  getDocDataSnap,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import CommentBlock from "../../../components/CommentBlock";
import HistoryModule from "../../../components/HistoryModule";
import SelectInput from "../Components/SelectInput";
import { FiX, FiEdit2, FiMessageSquare, FiSend } from "react-icons/fi";

export default function IncidentReportDetails({ data, setViewMode }) {
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = React.useState(data);
  const [editMode, setEditMode] = React.useState(false);
  const [newComment, setNewComment] = React.useState("");
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [comments, setComments] = React.useState(data.comments || []);
  const [updateHappened, setUpdateHappened] = React.useState(false);
  const [newStatus, setNewStatus] = React.useState(data.status || "");

  useEffect(() => {
    const fetchData = async () => {
      const doc = await getDocDataSnap("incidentReports", data.id, (doc) => {
        setFormData(doc);
        setComments(doc.comments || []);
        setNewStatus(doc.status || "");
      });
    };

    fetchData();
  }, []);

  const status = ["pending", "investigating", "resolved", "closed"];
  const statusOptions = status.map((item) => ({ value: item, label: item }));

  const handleSelect = (selected) => {
    setUpdateHappened(true);
    setFormData({ ...formData, status: selected.value });
  };

  const handleUpdateStatus = async () => {
    const updatedStatus = formData.status;
    setNewStatus(updatedStatus);

    const docObject = {
      ...formData,
      updatedBy: currentUser.uid,
      updatedName: currentUser.displayName,
      updatedEmail: currentUser.email,
      updateType: "Status is updated to " + updatedStatus,
      history: [
        ...(formData?.history || []),
        {
          action: "Status Updated",
          at: new Date().toLocaleString(),
          by: currentUser.displayName,
          to: updatedStatus,
        },
      ],
    };

    await setDocData("incidentReports", data.id, docObject);
    toast.success("Status Updated Successfully");
    setUpdateHappened(false);
  };

  const handleChange = (e) => {
    setNewComment(e.target.value);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData(data);
    toast.error("Edit Cancelled");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSaveChanges = async () => {
    setIsProcessing(true);

    const docObject = {
      ...formData,
      updatedBy: currentUser.uid,
      updatedName: currentUser.displayName,
      updatedEmail: currentUser.email,
      updatedAt: new Date().toLocaleString(),
      updateType: "Incident details updated",
      history: [
        ...(formData?.history || []),
        {
          action: "Updated incident details",
          at: new Date().toLocaleString(),
          by: currentUser.displayName,
        },
      ],
    };

    await setDocData("incidentReports", data.id, docObject);
    toast.success("Incident report updated successfully");
    setEditMode(false);
    setIsProcessing(false);
  };

  const handleSubmitComment = async () => {
    setIsProcessing(true);
    const newComments = [
      ...comments,
      {
        message: newComment,
        email: currentUser.email,
        createdBy: currentUser.displayName,
        createdAt: new Date().toLocaleString(),
      },
    ];

    const docObject = {
      ...formData,
      comments: newComments,
      history: [
        ...(formData?.history || []),
        {
          action: "Added a comment",
          at: new Date().toLocaleString(),
          by: currentUser.displayName,
        },
      ],
    };

    await setDocData("incidentReports", data.id, docObject);

    setComments(newComments);
    setNewComment("");
    setIsProcessing(false);
    toast.success("Comment added successfully");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "investigating":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "resolved":
        return "bg-green-100 text-green-800 border-green-200";
      case "closed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "pending":
      default:
        return "bg-amber-100 text-amber-800 border-amber-200";
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
            <div className="text-base sm:text-lg font-semibold text-[#363062]">
              Incident Report Details
            </div>
            <div className="flex items-center gap-2 sm:gap-4 mr-12">
              <button
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  editMode
                    ? "bg-red-500 text-white hover:bg-red-600 shadow-sm"
                    : "bg-[#363062] text-white hover:bg-opacity-90 shadow-sm"
                }`}
                onClick={() => {
                  if (editMode) {
                    handleCancelEdit();
                  } else {
                    setEditMode(true);
                  }
                }}
              >
                {editMode ? (
                  <>
                    <FiX className="w-4 h-4" />
                    Cancel Edit
                  </>
                ) : (
                  <>
                    <FiEdit2 className="w-4 h-4" />
                    Edit Incident Report
                  </>
                )}
              </button>

              {setViewMode !== undefined && (
                <button
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors absolute top-2 right-2"
                  onClick={() => setViewMode(false)}
                >
                  <FiX className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 sm:mb-6">
            <div
              className={`px-3 py-1.5 rounded-full border ${getStatusColor(
                newStatus
              )} font-medium capitalize text-xs sm:text-sm`}
            >
              {newStatus || "pending"}
            </div>
            {editMode && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <SelectInput
                  name={"status"}
                  placeholder={"Select Status"}
                  options={statusOptions}
                  onChange={(selected) => handleSelect(selected)}
                  value={statusOptions.find(
                    (item) => item.value === formData.status
                  )}
                  className="w-full sm:w-48"
                />
                <button
                  className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 text-sm"
                  disabled={!updateHappened}
                  onClick={handleUpdateStatus}
                >
                  Update Status
                </button>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Incident Details */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Incident Information Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Incident Information
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Type of Incident
                      </div>
                      {editMode ? (
                        <input
                          type="text"
                          name="incidentType"
                          className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]"
                          placeholder="Type of Incident"
                          value={formData.incidentType || ""}
                          onChange={handleInputChange}
                        />
                      ) : (
                        <div className="font-medium text-xs sm:text-sm">
                          {formData.incidentType || "N/A"}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Location
                      </div>
                      {editMode ? (
                        <input
                          type="text"
                          name="location"
                          className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]"
                          placeholder="Location"
                          value={formData.location || ""}
                          onChange={handleInputChange}
                        />
                      ) : (
                        <div className="font-medium text-xs sm:text-sm">
                          {formData.location || "N/A"}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Police Reported?
                      </div>
                      {editMode ? (
                        <select
                          name="policeReported"
                          className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]"
                          value={formData.policeReported || "No"}
                          onChange={handleInputChange}
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      ) : (
                        <div className="font-medium text-xs sm:text-sm">
                          {formData.policeReported || "No"}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Reported On
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.createdAt || new Date().toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm sm:text-base text-[#363062]">
                      Incident Description
                    </h4>
                    {editMode ? (
                      <textarea
                        rows="10"
                        name="description"
                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062] resize-none text-xs sm:text-sm"
                        placeholder="Description"
                        value={formData.description || ""}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 min-h-[150px] whitespace-pre-wrap text-xs sm:text-sm">
                        {formData.description || "No description provided"}
                      </div>
                    )}
                  </div>

                  {/* Save Changes Button */}
                  {editMode && (
                    <div className="mt-4 sm:mt-6 flex justify-end">
                      <button
                        className="px-4 py-2 bg-[#8FCD00] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        onClick={handleSaveChanges}
                        disabled={isProcessing}
                      >
                        {isProcessing ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062] flex items-center gap-2">
                    <FiMessageSquare className="w-4 h-4" />
                    Comments
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="mb-4 sm:mb-6">
                    <textarea
                      name="newComment"
                      className="w-full rounded-lg p-3 sm:p-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-transparent resize-none text-xs sm:text-sm"
                      rows="3"
                      onChange={handleChange}
                      placeholder="Write your comment here..."
                      value={newComment}
                    ></textarea>
                    <div className="flex justify-end mt-3">
                      <button
                        className="px-3 sm:px-4 py-2 bg-[#8FCD00] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 text-xs sm:text-sm"
                        onClick={handleSubmitComment}
                        disabled={!newComment.trim() || isProcessing}
                      >
                        <FiSend className="w-4 h-4" />
                        {isProcessing ? "Submitting..." : "Submit Comment"}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {comments && comments.length > 0 ? (
                      comments.map((comment, index) => (
                        <CommentBlock key={index} comment={comment} />
                      ))
                    ) : (
                      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg text-center text-xs sm:text-sm">
                        No comments yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - History */}
            <div className="space-y-4 sm:space-y-6">
              {/* Reporter Info Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Reporter Information
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Reported By
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.createdByName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Email
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.createdEmail || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Date Reported
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.createdAt || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* History Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    History
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <HistoryModule data={formData.history || []} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
